// frontend/src/components/projects/workspaces/TurkishProjectWorkspace.tsx
// Türkçe ve Edebiyat dersleri için Yaratıcı Yazarlık ve Metin İnceleme Atölyesi

import { useState, useEffect } from 'react';

export interface TurkishWorkData {
  topic: string;
  mainIdea: string;
  charactersOrStructure: string;
  storyText: string;
  literaryDevices: string;
}

interface TurkishProjectWorkspaceProps {
  initialContent: string;
  projectTitle: string;
  projectDescription: string | null;
  onChange: (formattedText: string) => void;
  disabled?: boolean;
}

export function TurkishProjectWorkspace({
  initialContent,
  projectTitle,
  projectDescription,
  onChange,
  disabled = false,
}: TurkishProjectWorkspaceProps) {
  const [data, setData] = useState<TurkishWorkData>(() => {
    try {
      if (initialContent.startsWith('{') && initialContent.includes('mainIdea')) {
        return JSON.parse(initialContent);
      }
    } catch {
      // Fallback
    }
    return {
      topic: '',
      mainIdea: '',
      charactersOrStructure: '',
      storyText: '',
      literaryDevices: '',
    };
  });

  const [activeTab, setActiveTab] = useState<'editor' | 'preview'>('editor');

  useEffect(() => {
    const formatted = `### 📚 TÜRKÇE & EDEBİYAT YARATICI YAZIM VE İNCELEME PROJESİ
**Proje Başlığı:** ${projectTitle}
**Konu & Tema:** ${data.topic || projectTitle}

#### 1. Ana Fikir ve Mesaj
${data.mainIdea || '(Belirtilmedi)'}

#### 2. Kurgu, Olay Örgüsü ve Karakterler
${data.charactersOrStructure || '(Kurgu detayları girilmedi)'}

#### 3. Özgün Edebi Metin / Hikaye
${data.storyText || '(Metin henüz yazılmadı)'}

#### 4. Dil Bilgisi, Söz Sanatları & Anlatım Özellikleri
${data.literaryDevices || '(Söz sanatı notu yok)'}
`;
    onChange(formatted);
  }, [data, projectTitle, onChange]);

  function updateField<K extends keyof TurkishWorkData>(key: K, value: TurkishWorkData[K]) {
    setData((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <div className="turkish-workspace-container">
      {/* Sol Panel: Türkçe Yazarlık ve İnceleme Formu */}
      <div className="turkish-editor-panel card">
        <div className="turkish-panel-header">
          <div className="turkish-badge">📚 Yaratıcı Yazarlık & Metin İnceleme Atölyesi</div>
          <div className="turkish-tabs">
            <button
              type="button"
              className={`turkish-tab-btn ${activeTab === 'editor' ? 'active' : ''}`}
              onClick={() => setActiveTab('editor')}
            >
              ✍️ Metin Editörü
            </button>
            <button
              type="button"
              className={`turkish-tab-btn ${activeTab === 'preview' ? 'active' : ''}`}
              onClick={() => setActiveTab('preview')}
            >
              📖 Kitap Önizleme
            </button>
          </div>
        </div>

        {activeTab === 'editor' ? (
          <div className="turkish-form-body">
            {/* Konu & Ana Fikir */}
            <div className="turkish-grid-2">
              <div className="turkish-form-group">
                <label className="turkish-label">
                  <span>1. Konu ve Tema</span>
                  <span className="turkish-hint">Örn: Cesaret, Doğa Sevgisi, Bilim Kurgu...</span>
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="Metnin ele aldığı ana konuyu belirt..."
                  value={data.topic}
                  onChange={(e) => updateField('topic', e.target.value)}
                  disabled={disabled}
                />
              </div>

              <div className="turkish-form-group">
                <label className="turkish-label">
                  <span>2. Ana Fikir (İletilmek İstenen Mesaj)</span>
                  <span className="turkish-hint">Örn: Birlikten kuvvet doğar, merak keşfetmeyi sağlar...</span>
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="Okuyucuya verilmek istenen temel düşünceyi yaz..."
                  value={data.mainIdea}
                  onChange={(e) => updateField('mainIdea', e.target.value)}
                  disabled={disabled}
                />
              </div>
            </div>

            {/* Olay Örgüsü & Karakterler */}
            <div className="turkish-form-group">
              <label className="turkish-label">
                <span>3. Olay Örgüsü, Karakterler ve Mekan Kurgusu</span>
                <span className="turkish-hint">Karakterlerin özellikleri, olayın geçtiği zaman ve yer bilgisi</span>
              </label>
              <textarea
                className="input turkish-textarea"
                rows={2}
                placeholder="Örn: Başkahraman Eren (14 yaşında meraklı bir kaşif), mekan: Gökyüzü Gözlemevi..."
                value={data.charactersOrStructure}
                onChange={(e) => updateField('charactersOrStructure', e.target.value)}
                disabled={disabled}
              />
            </div>

            {/* Özgün Metin / Hikaye */}
            <div className="turkish-form-group">
              <label className="turkish-label">
                <span>4. Özgün Hikaye / Deneme / Paragraf Metni</span>
                <span className="turkish-hint">Giriş, gelişme ve sonuç bölümlerine, noktalama ve imla kurallarına dikkat ederek yaz.</span>
              </label>
              <textarea
                className="input turkish-textarea"
                rows={7}
                placeholder="Hikayeni veya paragraf incelemeni buraya yaz..."
                value={data.storyText}
                onChange={(e) => updateField('storyText', e.target.value)}
                disabled={disabled}
              />
            </div>

            {/* Söz Sanatları & Dil Özellikleri */}
            <div className="turkish-form-group">
              <label className="turkish-label">
                <span>5. Söz Sanatları, Deyimler & Anlatım Teknikleri</span>
                <span className="turkish-hint">Metninde kullandığın benzetme, kişileştirme, tezat gibi edebi sanatlar veya deyimler</span>
              </label>
              <input
                type="text"
                className="input"
                placeholder="Örn: Rüzgar fısıldıyordu (Kişileştirme), Taş gibi ağır (Benzetme), Göz gezdirmek (Deyim)..."
                value={data.literaryDevices}
                onChange={(e) => updateField('literaryDevices', e.target.value)}
                disabled={disabled}
              />
            </div>
          </div>
        ) : (
          <div className="turkish-preview-book card">
            <div className="book-page-header">
              <span className="book-title">{projectTitle}</span>
              <span className="book-badge">Yayın Önizlemesi</span>
            </div>
            <div className="book-page-content">
              {data.topic && <div className="book-theme">📌 Tema: {data.topic}</div>}
              {data.mainIdea && <div className="book-idea">💡 Ana Fikir: {data.mainIdea}</div>}
              <div className="book-divider" />
              <div className="book-body-text">
                {data.storyText || '— Henüz bir metin yazılmadı —'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sağ Panel: Türkçe Yazım Rehberi */}
      <div className="turkish-side-panel">
        <div className="card turkish-guide-card">
          <h3>📚 Türkçe & Edebiyat Kriterleri</h3>
          <p className="turkish-guide-desc">
            Yaratıcı kurgu, zengin kelime dağarcığı ve doğru noktalama kullanımı değerlendirmede tam puan kazandırır.
          </p>
          <div className="turkish-checklist">
            <div className={`turkish-check-item ${data.mainIdea.trim() ? 'done' : ''}`}>
              <span className="check-icon">{data.mainIdea.trim() ? '✅' : '⚪'}</span>
              <div>
                <strong>Açık Bir Ana Fikir</strong>
                <p>Metnin ilettiği mesaj net olmalıdır.</p>
              </div>
            </div>
            <div className={`turkish-check-item ${data.storyText.trim().length > 60 ? 'done' : ''}`}>
              <span className="check-icon">{data.storyText.trim().length > 60 ? '✅' : '⚪'}</span>
              <div>
                <strong>Bölüm Bütünlüğü</strong>
                <p>Giriş, gelişme ve sonuç akıcı bağlanmalıdır.</p>
              </div>
            </div>
            <div className={`turkish-check-item ${data.literaryDevices.trim() ? 'done' : ''}`}>
              <span className="check-icon">{data.literaryDevices.trim() ? '✅' : '⚪'}</span>
              <div>
                <strong>Söz Sanatları & Zengin Anlatım</strong>
                <p>Deyimler ve benzetmelerle anlatımı güçlendirin.</p>
              </div>
            </div>
          </div>
        </div>

        {projectDescription && (
          <div className="card turkish-info-card">
            <h4>📌 Yazarlık Görevi</h4>
            <p>{projectDescription}</p>
          </div>
        )}
      </div>
    </div>
  );
}
